import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import InfoAlert from './components/infoAlert';
import PageHeading from 'components/heading';
import Table, { ColumnConfig } from 'components/table';
import {
  paymentProviderStatusFormatter,
  defaultProcessorCheckFormatter,
} from 'utils/status-formatter';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import { PaymentSolutionTab } from 'state/paymentConfig/type';
import { DEFAULT_PROCESSOR, DEFAULT_TICKET_NOTIFICATION } from 'utils/constants';

interface IProps {
  activeTab: PaymentSolutionTab;
  setActiveTab?: Dispatch<SetStateAction<PaymentSolutionTab>>;
  defaultProvider: PAYMENT_CONFIG.IProcessors | undefined;
  setDefaultProvider: Dispatch<
    SetStateAction<PAYMENT_CONFIG.IProcessors | undefined>
  >;
}

interface ITableData extends PAYMENT_CONFIG.IProcessors {
  _paymentMethod: string;
  _status: string;
  _defaultProcessor: string
  _defaultTicketNotification: string
}

export const checkEnabled = (
  provider: PAYMENT_CONFIG.DefaultProcessorData | undefined | null,
  list: PAYMENT_CONFIG.IProcessors[]
) => {
  if (list?.length > 0) {
    return list.some(
      (item) => item.processorName === provider?.processorName && item.isEnabled
    );
  }
  return false;
};

export const getPaymentFilterList = (
  list: PAYMENT_CONFIG.IProcessors[],
  filter: PaymentSolutionTab
) => {
  return list?.length > 0
    ? list?.filter((item) => {
        return item?.paymentMethod === filter && item?.isEnabled;
      })
    : [];
};

export const isDefaultForCategory = (
  processorId: string,
  defaultConfigurations: PAYMENT_CONFIG.IFetchDefaultConfigurations,
  category: string
) => {
  if (!defaultConfigurations?.data || defaultConfigurations.data.length === 0) {
    return false;
  }
  return defaultConfigurations.data.some(
    (config) =>
      config.category === category &&
      config.processorId === processorId
  );
};

const Home = ({
  activeTab,
  setActiveTab,
  defaultProvider,
  setDefaultProvider,
}: IProps): JSX.Element => {
  const { paymentConfig, updatedFilterList } = usePaymentConfig();

  const { paymentSolutions, defaultProcessor, defaultConfigurations } = paymentConfig;

  const [noProviderFlag, setNoProviderFlag] = useState(false);
  const [noApprovedProviderFlag, setNoApprovedProviderFlag] = useState(true);

  const changeTab = (tab: PaymentSolutionTab) => {
    setActiveTab?.(tab);
  };

  const getDefaultProcessorFilterList = (
    list: PAYMENT_CONFIG.IProcessors[],
    filter: string
  ) => {
    const filteredProcessorList = list?.find(
      (item) => item.processorId === filter && item.isEnabled
    );
    return filteredProcessorList;
  };

  const toggle = (
    tab: PaymentSolutionTab,
    processorId?: string,
    paymentType?: PaymentSolutionTab
  ) => {
    const paymentMethodFilter =
      paymentType !== undefined
        ? getPaymentFilterList(
            paymentSolutions?.processor?.processors !== null
              ? paymentSolutions?.processor?.processors
              : [],
            paymentType
          )
        : [];
    if (processorId) {
      const selectDefault = getDefaultProcessorFilterList(
        paymentMethodFilter,
        processorId
      );
      setDefaultProvider(selectDefault);
    }
    updatedFilterList(paymentMethodFilter);
    changeTab(tab);
  };

  const noApprovedProviderFlagCheck = (
    providerList: PAYMENT_CONFIG.IProcessors[]
  ) => {
    const hasAnyProvider = providerList.some((item) => item?.isEnabled);
    setNoApprovedProviderFlag(!hasAnyProvider);
  };

  const preprocessTableData = (
    data: PAYMENT_CONFIG.IProcessors[]
  ): ITableData[] => {
    return data.map(
      (row) =>
        ({
          ...row,
          _paymentMethod:
            row.paymentMethod !== null ? row.paymentMethod : 'ATM',
          _status: row.isEnabled ? 'APPROVED' : 'DISABLED',
        } as ITableData)
    );
  };

  const tableColumns: ColumnConfig<ITableData>[] = [
    {
      key: '_paymentMethod',
      label: 'Payment Solution',
      sortable: true,
      renderCell: (row) => (
        <div className="capitalize">{`Integrated ${row._paymentMethod}`}</div>
      ),
    },
    {
      key: 'processorName',
      label: 'Payment Provider',
      sortable: true,
      renderCell: (row) => (
        <div className="capitalize">{row.processorName}</div>
      ),
    },
    {
      key: '_status',
      label: 'Status',
      renderCell: (row) => paymentProviderStatusFormatter(row.isEnabled),
    },
    {
      key: '_defaultProcessor',
      label: 'Default',
      renderCell: (row) =>
        defaultProcessorCheckFormatter(
          isDefaultForCategory(row.processorId, defaultConfigurations, DEFAULT_PROCESSOR)
        ),
    },
    {
      key: '_defaultTicketNotification',
      label: 'PAYLINKS',
      renderCell: (row) =>
        defaultProcessorCheckFormatter(
          isDefaultForCategory(row.processorId, defaultConfigurations, DEFAULT_TICKET_NOTIFICATION)
        ),
    },
    {
      key: 'processorId',
      label: 'Actions',
      renderCell: (row) => (
        <Button
          type="button"
          variant="link"
          size="sm"
          disabled={!row.isEnabled}
          onClick={() =>
            toggle(
              row.paymentMethod as PaymentSolutionTab,
              row?.processorId,
              row?.paymentMethod as PaymentSolutionTab
            )
          }
          className='px-0'
        >
          Configure
        </Button>
      ),
    },
  ];

  useEffect(() => {
    if (
      paymentSolutions?.processor?.processors &&
      paymentSolutions?.processor?.processors?.length > 0
    ) {
      setNoProviderFlag(false);
    }
    if (paymentSolutions?.processor?.processors) {
      noApprovedProviderFlagCheck(paymentSolutions.processor.processors);
    }
    if (
      !paymentSolutions?.processor?.processors ||
      (paymentSolutions?.processor?.processors &&
        paymentSolutions?.processor?.processors?.length === 0)
    ) {
      setNoProviderFlag(true);
    }
  }, [paymentSolutions?.processor?.processors]);

  const tableData = preprocessTableData(
    paymentSolutions?.processor?.processors || []
  );

  return (
    <>
      {noProviderFlag ? (
        <InfoAlert>
          You are currently not approved for payments. Please contact your
          TreezPay Account Manager or
          <a
            className="ml-1 text-dark-green-link underline hover:no-underline"
            target="_blank"
            rel="noopener noreferrer"
            href="https://support.treez.io/hc/en-us/requests/new"
          >
            contact support
          </a>
          {' '}in case there are any queries.
        </InfoAlert>
      ) : (
        <>
          <PageHeading
            level="h2"
            heading="Payment solutions and providers"
            subHeading="Below is an overview of the payment solutions you have opted for and their payment providers."
          />
          {noApprovedProviderFlag && (
            <InfoAlert>
              You can start configuring the providers only after you have been
              approved.
            </InfoAlert>
          )}
          <div className="mt-3">
            <Table
              data={tableData}
              columns={tableColumns}
              isEmpty={!tableData.length}
              theme={{
                tableWidth: '900px',
                showTableBorder: true,
                tableGridLayout: '1fr 1fr auto 100px 100px 1fr',
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default Home;
